
declare const gtag: Function
import Vue from "vue";
import { Route } from "vue-router";
import rootStore from "@/state/store";
import optionsStore from "@/state/modules/options";
import partnersStore from "@/state/modules/partners";

import AppHeader from "@/components/AppHeader.vue";
import AppFooter from "@/components/AppFooter.vue";
import { adminUserPartner } from './state/modules/constants';

interface AppComponentData {
	authenticated: boolean;
	theme: "dark-theme" | "light-theme";
	errorMsg: String;
}

export default Vue.extend({
	components: {
		AppHeader,
		AppFooter,
	},
	page: {
		// All subcomponent titles will be injected into this template.
		titleTemplate(title: ((store: any) => string) | string): string {
			const siteName = "Partner Portal";
			const titleStr = (typeof title === "function") ? title(this.$store) : title;
			return titleStr ? `${titleStr} :: ${siteName}` : siteName;
		},
	},
	props: {
		configPromise: {
			// Expecting a promise, this allows polyfilled promises (anything with a then/catch)
			validator: (p: any) => p && typeof p.then === "function" && typeof p.catch === "function",
		},
	},
	data(): AppComponentData {
		return {
			authenticated: false,
			theme: this.$cookies.get("vivint-theme") || "light-theme",
			errorMsg: ""
		};
	},
	computed: {
		appClasses(): IDictionary<boolean> {
			const classes = {
				authenticated: !!this.authenticated,
				"dark-theme": this.theme === "dark-theme",
				"light-theme": this.theme === "light-theme",
				"error-page": false,
				"has-footer": ["order-entry"].indexOf(`${this.$route.name}`) < 0,
			};
			const routeName = `${this.$route.name}`;
			classes["error-page"] = ["404", "auth-error"].indexOf(routeName) > -1;
			return classes;
		},
		getUrl(): string {
			if (rootStore.state.config) {
				var routeName = this.$route.name;
				if (routeName === "order-entry") {
					return rootStore.state.config.oeToolUrl;
				}
				return "";
			}
			return "";
		},
		userManagerUrl(): string {
			return rootStore.getUserManagerUrl;
		},
		showUserTab(): boolean {
			return rootStore.getUserIsAdmin;
		},
		isSuperAdmin(): boolean {
			return rootStore.getUserIsSuperAdmin;
		},
		showStyleGuideTab(): boolean {
			const host = window.location.host;
			if (host && /(local|3200|3000|-dev.|-test.|-stg.)/.test(host)) {
				return rootStore.getIsItUser;
			}
			return false;
		},
		showMessage(): boolean {
			if (this.$route.name === "auth-error") {
				return false; // No message req"d for error page
			}
			if (this.$route.name === "404") {
				return false; // No message req"d for 404 page
			}
			return this.messages.length > 0;
		},
		messages(): IMessage[] {
			return rootStore.state.messages;
		},
	},
	watch: {
		// Every time the route changes, check the auth status
		$route(to) {
			const authPromise = this.configPromise
				.finally(() => {
					return this.checkAuthentication(to);
				});

			// When created, delay some actions until after authenticated
			authPromise.then(() => {
				if (optionsStore.state.employeeId.length === 0) {
					optionsStore.dispatchGetOptions();
				}
				// if we have userInfo and no userPartner, lookup the partner and set it as userPartner for filtering
				if (rootStore.state.userInfo) {
					if (rootStore.state.userInfo.office && !partnersStore.state.userPartner) {
						const lookupPartners: Array<string> = rootStore.state.userInfo.office;
						let foundValidPartner = false;

						lookupPartners.forEach(async lookupPartner => {
							const partnerId = lookupPartner.replace("-partner", "");

							await partnersStore.dispatchGetPartner(partnerId)
								.then(data => {
									if (data) {
										partnersStore.commitSetUserPartner(data);
										foundValidPartner = true;
									} else {
										this.errorMsg =	"Error: Invalid response.";
									}
								});

							if (!this.errorMsg) {
								this.errorMsg = foundValidPartner ? "" : "Error: Could not find valid partner.";
							}
						});
					} else if (!partnersStore.state.userPartner) {
						partnersStore.commitSetUserPartner(adminUserPartner);  // TODO create a constant for an IPartner to pass into here so that it knows
					}
				}
			});
		},
	},
	methods: {
		async checkAuthentication(to?: Route) {
			try {
				const previouslyLoggedIn = this.authenticated;
				gtag('event', 'checkAuth', { 'event_category': 'Auth', 'event_label': 'checkAuth'});
				this.authenticated = await this.$auth.isAuthenticated();
				const justLoggedIn = !previouslyLoggedIn && this.authenticated;
				if (justLoggedIn) {
					try {
						const oktaUserInfo: IUser = await this.$auth.getUser();
						const oktaAccessToken = await this.$auth.getAccessToken();
						rootStore.commitSetUser({
							user: oktaUserInfo,
							accessToken: oktaAccessToken,
						});
					} catch (e) {
						console.error(e); // tslint:disable-line:no-console
						gtag('event', 'oktaError', { 'event_category': 'Auth', 'event_label': 'checkAuth'});
						rootStore.commitClearUser();
						this.authenticated = false;
						rootStore.commitRemoveMessage("authenticating");
						this.$auth.loginRedirect();
					}
				}
				// If just logged in, or if new route doesn"t require auth, go ahead and clear auth message
				const skipAuth = (to && !to.meta.requiresAuth);
				if (justLoggedIn || skipAuth) {
					rootStore.commitRemoveMessage("authenticating");
				}
				const justLoggedOut = previouslyLoggedIn && !this.authenticated;
				if (justLoggedOut) {
					rootStore.commitClearUser();
					this.authenticated = false;
				}
				return rootStore.state.userInfo;
			} catch (e) {
				rootStore.commitClearUser();
				this.authenticated = false;
				gtag('event', 'authError', { 'event_category': 'Auth', 'event_label': 'checkAuth'});
				return rootStore.state.userInfo;
			}
		},
		logout() {
			// Show message, wait 1 sec for UX
			const msg: IMessage = {
				id: "logout",
				type: "info",
				message: "Logging out...",
				timeout: 0,
			};
			rootStore.commitAddMessage(msg);
			setTimeout(
				async () => {
					try {
						await this.$auth.logout();
						await this.checkAuthentication();
					} catch (e) {
						// It"s possible that the error just means user is already logged out.
						// Do nothing except log error
						console.error(e); // tslint:disable-line:no-console
					}
					rootStore.commitRemoveMessage("logout");
					// Redirect internal
					// this.$router.push({ name: "home" });
					// Redirect to login page
					this.$auth.loginRedirect();
				},
				1000,
			);
		},
		swapTheme() {
			const newTheme = (this.theme === "dark-theme") ? "light-theme" : "dark-theme";
			const host = window.location.host;
			if (host && /vivint\.com$/.test(host)) {
				this.$cookies.set("vivint-theme", newTheme, "1y", "/", ".vivint.com");
			} else {
				this.$cookies.set("vivint-theme", newTheme);
			}
			this.theme = newTheme;
			gtag('event', this.theme, { 'event_category': 'AppTheme', 'event_label': 'AppTheme'});
		},
		clearMessage(messageId: string) {
			rootStore.dispatchRemoveMessage(messageId);
		},
	},
});
